import React, { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@hqo/ui-atoms';
import { ChevronLeft, X } from 'lucide-react';
import { useIsSmallViewportWidth } from 'hooks/use-is-small-viewport-width.hook';

/*
 * ⚠️ WIRED BY HAND, and the alternative is a bug either way round. Radix points the surface at a
 * `Description` it expects to find and warns in development when there is none -- but the documented
 * way to say "deliberately none", passing `aria-describedby={undefined}`, ALSO unwires a description
 * that does exist, because it overrides the primitive's own value. Naming the element settles both:
 * the attribute points at it when there is one and is absent when there is not.
 */
const DESCRIPTION_ID = 'add-ons-dialog-description';

export interface AddOnsDialogProps {
  /** Names the surface for a screen reader, and titles it unless the body already does. */
  title: string;
  /** Set only when this step has one ABOVE it; the header then draws a chevron instead of the X. */
  onBack?: VoidFunction;
  /** Every other ending — the X, Esc, the overlay, and the drawer's own drag-down. */
  onClose: VoidFunction;
  /** Pinned below the scrolling body. The money and the CTA live here. */
  footer?: React.ReactNode;
  /** One short sentence under the title, when the step has something to explain. */
  description?: string;
  /*
   * VIS-2193. Drawn before the title, decorative (the title says it). The confirmation's check sits
   * here so the step has ONE heading rather than this title and a second one in the body.
   */
  titleIcon?: React.ReactNode;
  /*
   * Move focus to the title when the surface shows it. For a step reached by an action whose control
   * then unmounts (the order's submit), so a screen reader announces where the tenant landed.
   */
  focusTitle?: boolean;
  /*
   * A detour is on top of this surface; every dismissal is refused while it is. Billing's payment
   * sheet is federated with no shared React, so Radix's synthetic capture handler never sees its
   * events while the native `pointerdown` listener does — a click on it reads as outside.
   */
  isDetourOpen?: boolean;
  /*
   * Closes the detour; called for Esc only, and only while `isDetourOpen`. Capture listeners fire in
   * registration order with this surface first, so refusing alone takes Esc away from the detour
   * rather than leaving it to it. Unset, Esc does nothing: set it wherever `isDetourOpen` is set.
   */
  onDetourDismiss?: VoidFunction;
  children: React.ReactNode;
}

/**
 * RES-1367 — the add-on step's surface on the my-bookings page.
 *
 * ⛔ IT DOES NOT REPLACE `ReceiptBooking`, and must not grow into doing so. That wrapper serves nine
 * routes and the post-checkout add-on flow, where its sheet is the right shape and the tenant is
 * mid-payment. This one exists because the SAME step opened from the bookings pane landed in a
 * fixed-width RCL modal sitting across three columns of a layout it knew nothing about. The step
 * itself is unchanged — see `useBookingAddOns`, which both surfaces drive.
 *
 * ⚠️ THE HEADER CARRIES EXACTLY ONE CONTROL, which is the rule the sheet already follows: a chevron
 * when there is a step above, the X when there is not. Drawing both put two dismissals a few pixels
 * apart going to different places, which is what made the old surface feel like a trap.
 *
 * Esc, the overlay and (on the drawer) a drag downward all arrive through `onOpenChange`, so every
 * ending is the one handler rather than the X being special.
 */
export const AddOnsDialog = ({
  title,
  onBack,
  onClose,
  footer,
  description,
  titleIcon,
  focusTitle = false,
  isDetourOpen = false,
  onDetourDismiss,
  children,
}: AddOnsDialogProps): JSX.Element => {
  const intl = useIntl();
  const isMobileDevice = useIsSmallViewportWidth();

  /* See `focusTitle`. `tabIndex={-1}` below: focusable from here, never in the tab order. */
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (focusTitle) {
      titleRef.current?.focus();
    }
  }, [focusTitle]);

  /* The title with its icon, when the step gives one. One copy for both the drawer and the dialog. */
  const titleContent = titleIcon ? (
    <span className="flex min-w-0 items-center gap-2">
      <span className="flex shrink-0" aria-hidden="true">
        {titleIcon}
      </span>
      <span className="min-w-0 truncate">{title}</span>
    </span>
  ) : (
    title
  );

  /*
   * ⛔ THE BODY IS UNLOCKED BY HAND ON THE WAY OUT, and this is not belt and braces.
   *
   * A modal Radix layer sets `document.body.style.pointerEvents = 'none'` and restores whatever it
   * found there when it closes. That contract holds for ONE copy of the library. This app runs
   * several: three versions of `@radix-ui/react-dismissable-layer` resolve inside the bundle, and
   * billing's payment widget brings a fourth of its own across the module-federation boundary. Each
   * copy has its own module-level "what was there before" and its own layer registry, and they all
   * write the same one global property -- so a copy that locks while another already holds the lock
   * captures `none` as the original, and restores `none` on the way out. The page is then inert with
   * no dialog on screen, which is what a tenant reports as "the app stopped responding".
   *
   * ⚠️ GUARDED ON WHAT IS ACTUALLY LEFT, never unconditional. If any dialog is still open -- billing's
   * sheet outliving this surface, an alert over the top of it -- the lock is still wanted and
   * clearing it would hand clicks to a page that is supposed to be behind a modal. Only when nothing
   * remains is an inert body certainly wrong.
   *
   * ⚠️ NOT REPRODUCED IN JSDOM: a single copy of the library cleans up after itself correctly there,
   * which is the evidence that the fault is in having several rather than in the close path. So this
   * is a floor under the symptom, not a diagnosis of a specific sequence. The real fix is one copy.
   */
  useEffect(
    () => () => {
      const anotherLayerIsOpen = document.querySelector(
        '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]',
      );

      if (!anotherLayerIsOpen && document.body.style.pointerEvents === 'none') {
        document.body.style.pointerEvents = '';
      }
    },
    [],
  );

  /*
   * Covers the endings the Radix guards below do not: the header's X calls this directly, and on
   * mobile a drag downward reaches `onOpenChange` without passing through `onInteractOutside`.
   */
  const closeUnlessDetourIsOpen = useCallback(() => {
    if (isDetourOpen) {
      return;
    }

    onClose();
  }, [isDetourOpen, onClose]);

  /* Stepping back unmounts the step too, so it stands down for the same reason. */
  const goBackUnlessDetourIsOpen = useCallback(() => {
    if (isDetourOpen) {
      return;
    }

    onBack?.();
  }, [isDetourOpen, onBack]);

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        closeUnlessDetourIsOpen();
      }
    },
    [closeUnlessDetourIsOpen],
  );

  /*
   * `onInteractOutside` rather than `onPointerDownOutside`: it covers pointer and focus both, and vaul
   * forwards it untouched where it wraps the pointer event for its own drag handling.
   */
  const refuseWhileDetourIsOpen = useCallback(
    (event: { preventDefault: VoidFunction }) => {
      if (isDetourOpen) {
        event.preventDefault();
      }
    },
    [isDetourOpen],
  );

  /*
   * Handed on, not merely refused: this surface's keydown capture runs first, so a bare refusal makes
   * the key dead for both. `preventDefault` is unconditional here because `onDetourDismiss` is optional.
   */
  const handOffEscapeToTheDetour = useCallback(
    (event: KeyboardEvent) => {
      if (!isDetourOpen) {
        return;
      }

      event.preventDefault();
      onDetourDismiss?.();
    },
    [isDetourOpen, onDetourDismiss],
  );

  /*
   * Flipping `modal` below swaps Radix's modal content component for the non-modal one, which
   * remounts this subtree -- so the open autofocus fires again at the moment the detour appears and
   * would take focus straight back off it. Refused on the way in; on the way out the remount lets the
   * default run and focus returns here.
   */
  const keepFocusWithTheDetour = useCallback(
    (event: Event) => {
      if (isDetourOpen) {
        event.preventDefault();

        return;
      }

      /* VIS-2193: a surface that opens straight onto a `focusTitle` step lands on its title, not the X. */
      if (focusTitle) {
        event.preventDefault();
        titleRef.current?.focus();
      }
    },
    [isDetourOpen, focusTitle],
  );

  /*
   * ⛔ `data-slot="button"` IS LOAD-BEARING, not a shadcn affectation. app.css deliberately skips
   * Tailwind's Preflight -- this app renders tenant-authored HTML that relies on browser defaults --
   * so a bare <button> keeps the UA's own grey bevelled chrome underneath whatever utilities it
   * carries. The one piece of Preflight the app does keep is scoped to this attribute (see app.css,
   * RES-1367), so declaring it is how a button authored here gets the reset ui-atoms' own buttons
   * get. Without it these render as grey slabs.
   */
  const controlClasses =
    'inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-foreground transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none';

  /*
   * ⛔ A BACK CHEVRON IS NOT A CLOSE BUTTON, so it cannot be `DialogClose`. Radix would dismiss the
   * whole surface on top of whatever the step wanted, and the step's own target is the only one that
   * knows where "up" is from here.
   */
  const backControl = onBack ? (
    <button
      type="button"
      data-slot="button"
      onClick={goBackUnlessDetourIsOpen}
      aria-label={intl.formatMessage({ id: 'back' })}
      data-testid="add-ons-dialog-back"
      className={`-ml-1 ${controlClasses}`}
    >
      <ChevronLeft className="size-5" aria-hidden="true" />
    </button>
  ) : null;

  /*
   * ⚠️ OUR OWN X, where ui-atoms ships one. Its built-in close is a bare Radix `Close` carrying no
   * `data-slot`, so it wears the same UA bevel described above and cannot be reset from here. Drawing
   * it ourselves also keeps both header controls in one row rather than one in the flow and one
   * absolutely positioned over it. Fix queued for the package; this is not waiting on it.
   */
  const closeControl = onBack ? null : (
    <button
      type="button"
      data-slot="button"
      onClick={closeUnlessDetourIsOpen}
      aria-label={intl.formatMessage({ id: 'close' })}
      data-testid="add-ons-dialog-close"
      className={`-mr-1 ml-auto ${controlClasses}`}
    >
      <X className="size-5" aria-hidden="true" />
    </button>
  );

  if (isMobileDevice) {
    return (
      /*
        A detour is a foreign surface with its own focus trap and its own scroll lock. Modality is
        exclusive -- two of them fight -- so this one stands down while one is up: modal here holds
        focus against billing's card fields and lets `RemoveScroll` swallow wheel and touch inside a
        sheet this tree does not own.

        Keyed so the flip REMOUNTS: vaul 1.1.2's `Overlay` early-returns on `!modal` above a
        `useCallback`, so toggling it on a live drawer changes the hook count and React throws.
        Radix's `Dialog` swaps its own content component and needs no key.
      */
      <Drawer key={isDetourOpen ? 'detour' : 'alone'} open modal={!isDetourOpen} onOpenChange={onOpenChange}>
        {/*
          ⚠️ `max-h` rather than a height: a one-item picker should not open a sheet to the top of the
          screen. The body below is the only thing that scrolls, so the drawer grows to its content
          and stops.
        */}
        <DrawerContent
          className="flex max-h-[92vh] flex-col"
          aria-describedby={description ? DESCRIPTION_ID : undefined}
          onInteractOutside={refuseWhileDetourIsOpen}
          onEscapeKeyDown={handOffEscapeToTheDetour}
          onOpenAutoFocus={keepFocusWithTheDetour}
          data-testid="add-ons-dialog"
        >
          <DrawerHeader className="flex flex-row items-center gap-2 border-b border-border px-4 pt-2 pb-3 text-left">
            {backControl}
            <DrawerTitle asChild className="min-w-0 flex-1 truncate text-base font-semibold focus:outline-none">
              {/* `asChild`: ui-atoms' title does not forward a ref, and focus needs one. */}
              <h2 ref={titleRef} tabIndex={-1}>
                {titleContent}
              </h2>
            </DrawerTitle>
            {closeControl}
          </DrawerHeader>
          {description && (
            <DrawerDescription id={DESCRIPTION_ID} className="px-4 pt-3 text-sm text-muted-foreground">
              {description}
            </DrawerDescription>
          )}
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4" data-testid="add-ons-dialog-body">
            {children}
          </div>
          {footer && (
            <DrawerFooter className="border-t border-border px-4 pt-3 pb-4" data-testid="add-ons-dialog-footer">
              {footer}
            </DrawerFooter>
          )}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    /* Stands down while a detour is up, for the reason given on the drawer above. */
    <Dialog open modal={!isDetourOpen} onOpenChange={onOpenChange}>
      {/*
        ⛔ `p-0` AND A COLUMN, because the padding has to belong to the three bands rather than to the
        surface: a single padded box cannot have a body that scrolls under a header and a footer that
        stay put, which is the whole shape this step needs.

        The built-in X is withheld whenever the step named a back target — see the note above.
      */}
      <DialogContent
        showCloseButton={false}
        aria-describedby={description ? DESCRIPTION_ID : undefined}
        className="flex max-h-[85vh] w-[calc(100%-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-[560px]"
        onInteractOutside={refuseWhileDetourIsOpen}
        onEscapeKeyDown={handOffEscapeToTheDetour}
        onOpenAutoFocus={keepFocusWithTheDetour}
        data-testid="add-ons-dialog"
      >
        <DialogHeader className="flex flex-row items-center gap-2 border-b border-border px-6 py-4 text-left">
          {backControl}
          <DialogTitle asChild className="min-w-0 flex-1 truncate text-base font-semibold focus:outline-none">
            {/* `asChild`: ui-atoms' title does not forward a ref, and focus needs one. */}
            <h2 ref={titleRef} tabIndex={-1}>
              {titleContent}
            </h2>
          </DialogTitle>
          {closeControl}
        </DialogHeader>
        {description && (
          <DialogDescription id={DESCRIPTION_ID} className="px-6 pt-4 text-sm text-muted-foreground">
            {description}
          </DialogDescription>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5" data-testid="add-ons-dialog-body">
          {children}
        </div>
        {footer && (
          <DialogFooter className="border-t border-border px-6 py-4" data-testid="add-ons-dialog-footer">
            {footer}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
